import { getEnvProperty, PROPERTIES } from "./environment-service"
import { FRONT_VERSION_HEADER, frontUpdate } from "./front-update"

export interface RequestParam {
  key: string
  value: string | number | boolean | undefined | null
}

export interface RequestParams {
  [key: string]: string | number | boolean | undefined | null
}

/**
 * The JSON of a result, or — on a 404, the absence the app expects (« pas encore calculé ») — an error carrying the code
 * of its body, the absence code when it has none. {@link useHttp} lets a 404 through without an error nor a snackbar,
 * but its body is an error payload, not the result: the caller gets the code, to tell the absence from another one
 * (US 2392: a recalculation in progress).
 */
export async function resultOrTypedAbsence<T>(response: Response, absenceCode: string): Promise<T> {
  if (response.status === 404) {
    const body: { message?: string } | undefined = await response.json().catch(() => undefined)
    throw new Error(body?.message ?? absenceCode)
  }
  return response.json()
}

/**
 * Parses an optional JSON resource: the backend answers its absence (« pas encore calculé ») with a 404, which
 * {@link useHttp} lets through without an error nor a snackbar, read here as null.
 */
export async function parseNullableJsonResponse<T>(responsePromise: Promise<Response>): Promise<T | null> {
  const response = await responsePromise
  if (response.status === 404) {
    return null
  }
  return response.json()
}

export async function retry(
  input: RequestInfo,
  token: string | undefined,
  headers: Headers,
  options?: RequestInit
): Promise<Response> {
  if (options && options.headers && token) {
    headers.set("Authorization", `Bearer ${token}`)
    options.headers = headers
  }
  return fetch(input, options)
}

export function addRequestParams(
  url: string,
  token: string | undefined,
  requestParams?: RequestParam[]
): { finalUrl: RequestInfo; headers: Headers } {
  let finalUrl = getEnvProperty(PROPERTIES.VITE_APP_API_URL) + url
  if (requestParams && requestParams.length > 0) {
    finalUrl += "?"
    requestParams.forEach((requestParam, index) => {
      if (index !== 0) {
        finalUrl += "&"
      }
      // Encoded: a « + » would reach the server as a space (an email alias), a « & » or a « # » would cut the value
      finalUrl += `${encodeURIComponent(requestParam.key)}=${encodeURIComponent(String(requestParam.value))}`
    })
  }
  const headers = new Headers()

  if (token !== undefined) {
    headers.set("Authorization", `Bearer ${token}`)
  }
  // The backend refuses the request of a page left open on the previous version (front-update.ts)
  const frontVersion = frontUpdate.versionHeaderValue()
  if (frontVersion !== undefined) {
    headers.set(FRONT_VERSION_HEADER, frontVersion)
  }

  return { finalUrl, headers }
}

/**
 * Error code raised when the reverse proxy (nginx) rejects a request before it reaches the API because its
 * headers no longer fit in the proxy buffers (8 KB per header line by default). In practice the culprit is the
 * Cookie header: Cognito sessions that were never cleaned up pile up in the browser, one full token set
 * (id + access + refresh token + user data, about 6 KB) per user, until the limit is exceeded.
 */
export const REQUEST_HEADER_TOO_LARGE_ERROR_CODE = "REQUEST_HEADER_TOO_LARGE"

// nginx answers with a 400 and an HTML page titled "Request Header Or Cookie Too Large"; the standard status is 431.
const NGINX_HEADER_TOO_LARGE_MARKER = /Request Header Or Cookie Too Large/i

export function isRequestHeaderTooLarge(status: number, body: string): boolean {
  return status === 431 || (status === 400 && NGINX_HEADER_TOO_LARGE_MARKER.test(body))
}

export function resolveUrl(urlTemplate: string, params: (string | undefined)[], requestParams?: RequestParams): string {
  let url = urlTemplate
  const placeholders = urlTemplate.match(/:[a-z]+/gi) ?? []

  placeholders.forEach((placeholder, index) => {
    const pathVariable = params[index]
    if (index < params.length && pathVariable !== undefined) {
      url = url.replace(placeholder, pathVariable.toString())
    }
  })

  if (requestParams) {
    const queryStrs: string[] = []
    for (const key in requestParams) {
      if (Object.hasOwn(requestParams, key)) {
        const queryParam = requestParams[key]
        if (queryParam !== undefined && queryParam !== null) {
          queryStrs.push(`${encodeURIComponent(key)}=${encodeURIComponent(queryParam.toString())}`)
        }
      }
    }
    if (queryStrs.length) {
      url += `?${queryStrs.join("&")}`
    }
  }

  return url
}
